import { computed } from '@vue/composition-api'
import { now } from 'lodash'
import CollectionModule from '@/store/stores/collectionModule/CollectionModule'
import VideoPlatformContentsDocument from '@/store/stores/collectionModule/documents/videoPlatformContents/VideoPlatformContentsDocument'
import CustomAttributeDataDocument from '@/store/stores/collectionModule/documents/customAttribute/CustomAttributeDataDocument'

/**
 * 動画コンテンツを操作するための処理
 */
export default function useVideoContents() {
  const videoContentsCollectionModule = CollectionModule.createStore(VideoPlatformContentsDocument)
  const autoLinkVideoContentsCollectionModule = CollectionModule.createStore(
    VideoPlatformContentsDocument,
  )

  /**
   * 管理者登録 表示期間指定ありのリクエストオプション
   * @param currentDate 現在日時
   */
  const getAdminWithPeriodOptions = (currentDate: number) => ({
    query: {
      filter: {
        $and: [
          {
            $and: [{ startDate: { $lte: currentDate } }, { endDate: { $gte: currentDate } }],
          },
          {
            $or: [
              { videoType: 'adminRegisterYouTube_sfOfficial' },
              { videoType: 'adminRegisterYouTube_membersOnly' },
              { videoType: 'adminRegisterYouTube_motorsport' },
            ],
          },
        ],
      },
    },
    isAppendData: true,
  })

  /**
   * 管理者登録 表示期間指定なしのリクエストオプション
   * SUPER FORMULA 公式
   * @param limit 取得件数
   */
  const getAdminNoPeriodSfOfficialOptions = (limit: number) => ({
    query: {
      filter: {
        $and: [
          {
            $and: [{ startDate: null }, { endDate: null }],
          },
          { videoType: 'adminRegisterYouTube_sfOfficial' },
        ],
      },
      sort: '-_createdDate',
      limit: limit || 10,
    },
    isAppendData: true,
  })

  /**
   * 管理者登録 表示期間指定なしのリクエストオプション
   * 会員限定
   * @param limit 取得件数
   */
  const getAdminNoPeriodMembersOnlyOptions = (limit: number) => ({
    query: {
      filter: {
        $and: [
          {
            $and: [{ startDate: null }, { endDate: null }],
          },
          { videoType: 'adminRegisterYouTube_membersOnly' },
        ],
      },
      sort: '-_createdDate',
      limit: limit || 10,
    },
    isAppendData: true,
  })

  /**
   * 管理者登録 表示期間指定なしのリクエストオプション
   * モータースポーツ
   * @param limit 取得件数
   */
  const getAdminNoPeriodMotorsportOptions = (limit: number) => ({
    query: {
      filter: {
        $and: [
          {
            $and: [{ startDate: null }, { endDate: null }],
          },
          { videoType: 'adminRegisterYouTube_motorsport' },
        ],
      },
      sort: '-_createdDate',
      limit: limit || 10,
    },
    isAppendData: true,
  })

  /**
   * 管理者登録 YouTube動画コンテンツ情報一覧取得
   * @param limitsByTargetId targetIdごとに管理された取得件数
   *  - targetIdは表示場所を入れている。
   */
  const fetchAdminRegisterVideoContents = async (
    limitsByTargetId: Record<string, CustomAttributeDataDocument>,
  ) => {
    const currentDate = now()
    const sfOfficialLimit = limitsByTargetId.adminRegisterYouTube_sfOfficial?.displayLimit ?? 0
    const membersOnlyLimit = limitsByTargetId.adminRegisterYouTube_membersOnly?.displayLimit ?? 0
    const motorsportLimit = limitsByTargetId.adminRegisterYouTube_motorsport?.displayLimit ?? 0

    return Promise.all([
      videoContentsCollectionModule.fetch(getAdminWithPeriodOptions(currentDate)),
      videoContentsCollectionModule.fetch(getAdminNoPeriodSfOfficialOptions(sfOfficialLimit)),
      videoContentsCollectionModule.fetch(getAdminNoPeriodMembersOnlyOptions(membersOnlyLimit)),
      videoContentsCollectionModule.fetch(getAdminNoPeriodMotorsportOptions(motorsportLimit)),
    ])
  }

  /**
   * 自動登録のリクエストオプション
   * SUPER FORMULA 公式
   */
  const getAutoLinkSfOfficialOptions = () => ({
    query: {
      filter: {
        videoType: 'autoLinkYouTube_sfOfficial',
      },
      sort: '-startDate',
      limit: 10,
    },
    isAppendData: true,
  })

  /**
   * 自動登録のリクエストオプション
   * モータースポーツ
   */
  const getAutoLinkMotorsportOptions = () => ({
    query: {
      filter: {
        videoType: 'autoLinkYouTube_motorsport',
      },
      sort: '-startDate',
      limit: 10,
    },
    isAppendData: true,
  })

  /**
   * 自動で登録 YouTube動画コンテンツ情報一覧取得
   */
  const fetchAutoLinkVideoContents = async () =>
    Promise.all([
      autoLinkVideoContentsCollectionModule.fetch(getAutoLinkSfOfficialOptions()),
      autoLinkVideoContentsCollectionModule.fetch(getAutoLinkMotorsportOptions()),
    ])

  /**
   * 動画コンテンツ情報一覧
   */
  const videoContents = computed(() => [
    ...videoContentsCollectionModule.data,
    ...autoLinkVideoContentsCollectionModule.data,
  ])

  /**
   * 取得した動画コンテンツ情報をクリアする
   */
  const clearVideoContents = () => {
    videoContentsCollectionModule.clearData()
    autoLinkVideoContentsCollectionModule.clearData()
  }

  return {
    fetchAdminRegisterVideoContents,
    fetchAutoLinkVideoContents,
    videoContents,
    clearVideoContents,
  }
}
